import * as React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router-dom";
import { useAPI } from "components/lib";
import InitialInformationSkeleton from "../runQuote/components/InitialInformationSkeleton";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  AlertTriangle,
  Ban,
  Clock,
  FileText,
  Shield,
  Car,
  User,
  ClipboardList,
  Gavel,
  CheckCircle2,
} from "lucide-react";
import { QuoteUiProvider } from "./components/QuoteUiContext";
import { MultiStepFormLayout } from "./components/MultiStepFormLayout";
import { DEFAULTS, RENTERS_DEFAULTS } from "./utils/defaults";
import {
  FullSchema,
  FullSchemaBase,
  FullSchemaRenters,
  FullSchemaRentersBase,
} from "./utils/schema";
import { InitialInfo } from "./components/steps/InitialInfo";
import { InsuredInfo } from "./components/steps/InsuredInfo";
import { Vehicles } from "./components/steps/Vehicles";
import { Drivers } from "./components/steps/Drivers";
import { InsuredHistory } from "./components/steps/InsuredHistory";
import { Coverages } from "./components/steps/Coverages";
import { RentersInsuredInfo } from "./components/steps/RentersInsuredInfo";
import { RentersHistory } from "./components/steps/RentersHistory";
import { RentersCoverages } from "./components/steps/RentersCoverages";
import { ReviewAndSubmit } from "./components/steps/ReviewAndSubmit";

export function IndividualQuote() {
  const location = useLocation();

  const quoteType = location?.state?.data?.quoteType || "personal_auto";
  const navigate = useNavigate();

  React.useEffect(() => {
    if (quoteType === "renters") {
      navigate("/app/quote/individual", {
        state: { data: { quoteType: "personal_auto" } },
        replace: true,
      });
    }
  }, [quoteType, navigate]);

  const user = useAPI("/api/user");
  const producers = useAPI("/api/account");

  const [missingData, setMissingData] = React.useState(false);
  const [agencyDisabled, setAgencyDisabled] = React.useState(false);
  const [documentsPendingReview, setDocumentsPendingReview] = React.useState(false);

  const isRenters = quoteType === "renters";
  const formDefaults = React.useMemo(
    () => (isRenters ? RENTERS_DEFAULTS : DEFAULTS),
    [isRenters]
  );
  const schema = isRenters ? FullSchemaRenters : FullSchema;
  const schemaBase = isRenters ? FullSchemaRentersBase : FullSchemaBase;

  const methods = useForm({
    defaultValues: formDefaults,
    resolver: zodResolver(schema),
    mode: "all",
    reValidateMode: "all",
    criteriaMode: "all",
    shouldUnregister: false,
    delayError: 120,
  });

  React.useEffect(() => {
    methods.reset(formDefaults);
  }, [methods, formDefaults]);

  React.useEffect(() => {
    if (!producers?.data) return;

    const data = producers.data;
    if (data.documents_missing) {
      window.location.href = "/app/onboarding";
      return;
    }
    if (data.documents_pending_review) setDocumentsPendingReview(true);
    if (data.status === "partial_disabled" || data.status === "total_disabled") {
      setAgencyDisabled(true);
    }

    if (
      data.address1 === null ||
      data.address2 === null ||
      data.city === null ||
      data.state === null ||
      data.zip === null ||
      !data.certificate_contacts ||
      data.certificate_contacts.length < 1
    ) {
      setMissingData(true);
    }
  }, [producers?.data]);

  React.useEffect(() => {
    methods.setValue("step1.quoteType", quoteType, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [methods, quoteType]);

  const isBootstrapping = user.loading || producers.loading;

  const personalSteps = React.useMemo(
    () => [
      { id: 1, label: "Initial Information", icon: FileText },
      { id: 2, label: "Insured", icon: Shield },
      { id: 3, label: "Vehicles", icon: Car },
      { id: 4, label: "Drivers", icon: User },
      { id: 5, label: "Insured History", icon: ClipboardList },
      { id: 6, label: "Coverages", icon: Gavel },
      { id: 7, label: "Review & Submit", icon: CheckCircle2 },
    ],
    []
  );

  const rentersSteps = React.useMemo(
    () => [
      { id: 1, label: "Initial Information", icon: FileText },
      { id: 2, label: "Insured", icon: Shield },
      { id: 3, label: "History", icon: ClipboardList },
      { id: 4, label: "Coverages", icon: Gavel },
      { id: 5, label: "Review & Submit", icon: CheckCircle2 },
    ],
    []
  );

  const buildStepComponents = React.useCallback(
    ({ submitted, bannerText, producerOptions }) => {
      if (isRenters) {
        return {
          1: <InitialInfo user={user} producers={producers} />,
          2: <RentersInsuredInfo />,
          3: <RentersHistory />,
          4: <RentersCoverages />,
          5: (
            <ReviewAndSubmit
              isRenters
              submitted={submitted}
              bannerText={bannerText}
              producerOptions={producerOptions}
            />
          ),
        };
      }

      return {
        1: <InitialInfo user={user} producers={producers} />,
        2: <InsuredInfo />,
        3: <Vehicles />,
        4: <Drivers />,
        5: <InsuredHistory />,
        6: <Coverages />,
        7: (
          <ReviewAndSubmit
            submitted={submitted}
            bannerText={bannerText}
            producerOptions={producerOptions}
          />
        ),
      };
    },
    [isRenters, user, producers]
  );

  if (isBootstrapping) {
    return <InitialInformationSkeleton />;
  }

  if (missingData) {
    return (
      <div className="flex justify-center items-center h-full">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>Missing Information</AlertTitle>
          <AlertDescription>
            Your agency is missing some required data. Please fill out address
            and certificate contact information at <strong>Profile &gt; Company</strong>.
            <div>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => (window.location.href = "/app/account/company")}
              >
                Change
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (agencyDisabled) {
    return (
      <div className="flex justify-center items-center h-full">
        <Alert variant="destructive" className="max-w-md">
          <Ban className="h-5 w-5" />
          <AlertTitle>Agency Disabled</AlertTitle>
          <AlertDescription>
            Your agency has been disabled. If you need help, contact:{" "}
            <strong>hello@coverbadger.com</strong>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (documentsPendingReview) {
    return (
      <div className="flex justify-center items-center h-full">
        <Alert className="border-yellow-400 text-yellow-700 bg-yellow-50 max-w-md">
          <Clock className="h-5 w-5 text-yellow-600" />
          <AlertTitle>Documents Under Review</AlertTitle>
          <AlertDescription>
            Your documents are currently under review. If you need help, contact:{" "}
            <strong>hello@coverbadger.com</strong>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const steps = isRenters ? rentersSteps : personalSteps;

  return (
    <div className="min-h-[calc(100vh-(160px+3.125em))] p-6 bg-white mb-16">
      <FormProvider {...methods}>
        <QuoteUiProvider steps={steps}>
          <MultiStepFormLayout
            user={user}
            producers={producers}
            methods={methods}
            schema={schema}
            schemaBase={schemaBase}
            stepComponents={buildStepComponents}
          />
        </QuoteUiProvider>
      </FormProvider>
    </div>
  );
}
